import React from 'react';
import { MdChevronRight, MdClose, MdInfoOutline } from 'react-icons/md';
// Imported from its own module rather than the `components` barrel: that barrel
// re-exports ChatLayout, so pulling it in from here would close a cycle back
// onto this directory.
import Circular from '../Loader/Circular';

/**
 * Details-panel primitives, shared by the direct-message pane and the group
 * pane.
 *
 * Both panes grew their own copy of this layout out of antd grid rows with
 * per-block `mt-1`/`mt-2`/`mt-4`/`p-4` margins, so no two blocks in the column
 * sat on the same rhythm and the two panes did not match each other. The panel
 * is one scrolling stack here, with a fixed header and a single spacing scale.
 */

/** Fixed header: what this panel is, and the way out of it. */
export const InfoHeader = ({ title = 'Details', onClose }) => (
  <div className="info-panel-head">
    <span className="info-panel-head-glyph">
      <MdInfoOutline size={17} />
    </span>
    <span className="info-panel-head-title">{title}</span>
    {onClose && (
      <button type="button" className="modal-close" title="Close" onClick={onClose}>
        <MdClose size={15} />
      </button>
    )}
  </div>
);

/** Scrolling body. Owns the column's padding and the gap between blocks. */
export const InfoBody = ({ children, onScroll }) => (
  <div className="info-panel-body custom-scrollbar" onScroll={onScroll}>
    {children}
  </div>
);

/**
 * Identity block: avatar, name, and an optional status pill.
 *
 * `avatar` is a node rather than a URL because the call sites pick between an
 * antd `Image`, an `Avatar` fallback and a tinted anonymous placeholder.
 */
export const InfoIdentity = ({ avatar, name, sub, status }) => (
  <div className="info-identity">
    {avatar}
    <div className="info-identity-text">
      <div className="info-name">{name}</div>
      {sub && <div className="info-sub">{sub}</div>}
    </div>
    {status}
  </div>
);

/** Presence / metadata pill under the name. */
export const InfoStatusPill = ({ online, children }) => (
  <span className="info-status-pill">
    {typeof online === 'boolean' && (
      <span className={`presence-dot ${online ? 'is-online' : 'is-offline'}`} />
    )}
    {children}
  </span>
);

/** Row of quick actions — call, video, and whatever the pane's third verb is. */
export const InfoActions = ({ children }) => <div className="info-actions">{children}</div>;

export const InfoAction = ({ icon, label, tone, onClick }) => (
  <button type="button" className={`info-action ${tone ? `is-${tone}` : ''}`} onClick={onClick}>
    {icon}
    {label}
  </button>
);

/** Labelled block: an uppercase eyebrow over its content. */
export const InfoField = ({ label, hint, children }) => (
  <div className="info-field">
    <div className="info-field-head">
      <span className="section-label">{label}</span>
      {hint && <span className="info-field-hint">{hint}</span>}
    </div>
    {children}
  </div>
);

/** Read-only value, for a description or a share link. */
export const InfoReadout = ({ children, action, multiline = false }) => (
  <div className={`info-readout ${multiline ? 'is-multiline' : ''}`}>
    <span className="info-readout-value">{children}</span>
    {action}
  </div>
);

/**
 * Collapsible section. The header is a real button with the count beside the
 * label, so the disclosure state is reachable by keyboard and the count does
 * not have to be baked into the label string.
 */
export const InfoSection = ({ label, count, open, onToggle, children, action }) => (
  <div className="info-field">
    <button
      type="button"
      className="info-section-head"
      aria-expanded={Boolean(open)}
      onClick={onToggle}
    >
      <span className="section-label">{label}</span>
      {count !== undefined && count !== null && <span className="info-section-count">{count}</span>}
      <span className="info-section-rule" />
      <span className={`info-section-chevron ${open ? 'is-open' : ''}`}>
        <MdChevronRight size={16} />
      </span>
    </button>

    {/* Always rendered, revealed by the `is-open` class rather than mounted on
        demand: a section that only exists while open has nothing to animate
        from. The grid trick in motion.css (`0fr` → `1fr`) is what makes the
        reveal animatable without putting `height` on the critical path.

        `inert` and `hidden` do the work the unmount used to: a closed section
        keeps no focusable controls and stays out of the accessibility tree. */}
    <div className={`info-section-reveal ${open ? 'is-open' : ''}`} inert={open ? undefined : ''}>
      <div className="info-section-body">
        {action}
        {children}
      </div>
    </div>
  </div>
);

/** Nothing-here copy inside a section. */
export const InfoEmpty = ({ children }) => <div className="info-empty">{children}</div>;

/** A person in a member list. */
export const MemberRow = ({ avatar, name, meta, trailing }) => (
  <div className="member-row">
    {avatar}
    <span className="member-row-text">
      <span className="member-row-name">{name}</span>
      {meta && <span className="member-row-meta">{meta}</span>}
    </span>
    {trailing}
  </div>
);

/**
 * Shared media, as an even grid of squares.
 *
 * Both panes previously laid this out with antd column spans that resolved to
 * `lg={24}` — one full-width item per row inside a 320px column — and sized
 * each tile in `rem`, so the grid overflowed its own panel.
 */
export const MediaGrid = ({ loading, isEmpty, emptyText, children }) => {
  if (loading) {
    return (
      <div className="info-media-loading">
        <Circular size={40} />
      </div>
    );
  }

  if (isEmpty) return <InfoEmpty>{emptyText}</InfoEmpty>;

  return <div className="info-media-grid">{children}</div>;
};

export const MediaTile = ({ children, label }) => (
  <div className="info-media-tile">
    {children}
    {label && <span className="info-media-tile-label">{label}</span>}
  </div>
);

/** Full-width secondary action at the foot of the panel. */
export const InfoButton = ({ children, icon, tone, onClick }) => (
  <button type="button" className={`info-button ${tone ? `is-${tone}` : ''}`} onClick={onClick}>
    {icon}
    {children}
  </button>
);
